import {
  isDateRange,
  isMultiSelect,
  isSingleSelect,
  dateRangesAreEqual,
  multiSelectAreEqual,
  DateRange,
  FilterInputData,
  FilterName,
  HIDDEN_FILTERS,
} from "./types";
import {
  DateRangeInput,
  StudentListFilterInput,
  RiseStudentListFiltersInput,
} from "__generated__/types";
import { FunnelStateMaps } from "@/util/funnelGroups";
import { ParsedQs } from "qs";
import { JoinStateGroups } from "@/util/joinStateGroups";

export class FilterState {
  readonly data: Partial<FilterInputData>;

  constructor(data: Partial<FilterInputData>) {
    this.data = Object.freeze(data);
  }

  activeFilterCount() {
    return Object.keys(this.removeEmptyFields(this.data)).filter(
      (filterName) => !HIDDEN_FILTERS.includes(filterName as FilterName),
    ).length;
  }

  getActiveFilters() {
    return Object.fromEntries(
      Object.entries(this.removeEmptyFields(this.data)).filter(
        ([filterName]) => !HIDDEN_FILTERS.includes(filterName as FilterName),
      ),
    );
  }

  // returns new FilterState object with key removed
  removeFilter(name: FilterName): FilterState {
    return new FilterState(
      Object.fromEntries(
        Object.entries(this.data).filter(([key]) => key !== name),
      ),
    );
  }

  removeEmptyFields(filterState: Partial<FilterInputData>) {
    type values = (typeof filterState)[keyof FilterInputData];
    return Object.entries(filterState).reduce(
      (acc, [name, value]) => {
        if (
          (isMultiSelect(value) && value.length > 0) ||
          (isDateRange(value) && Object.keys(value).length > 0) ||
          (isSingleSelect(value) && value !== null)
        ) {
          acc[name] = value;
        }
        return acc;
      },
      {} as Record<string, values>,
    ) as Partial<FilterInputData>;
  }

  isEqual(state: FilterState): boolean {
    const thisStatePruned = this.removeEmptyFields(this.data);
    const thatStatePruned = this.removeEmptyFields(state.data);

    if (
      Object.keys(thatStatePruned).length !==
      Object.keys(thisStatePruned).length
    )
      return false;

    let filterName: keyof FilterInputData;

    for (filterName in thisStatePruned) {
      const [value, thatValue] = [
        thisStatePruned[filterName],
        thatStatePruned[filterName],
      ];
      if (
        (isMultiSelect(value) &&
          (!isMultiSelect(thatValue) ||
            !multiSelectAreEqual(value, thatValue))) ||
        (isDateRange(value) &&
          (!isDateRange(thatValue) || !dateRangesAreEqual(value, thatValue))) ||
        (isSingleSelect(value) &&
          (!isSingleSelect(value) || value !== thatValue))
      ) {
        return false;
      }
    }
    return true;
  }

  getQueryFilter(): StudentListFilterInput {
    const queryData = this.removeEmptyFields(this.data);

    return {
      ...this.formatRiseFilters(queryData),
      studentType: queryData.studentType ? [queryData.studentType] : undefined,
      country: queryData.country ? [queryData.country] : undefined,
      cohortYear: queryData.cohortYear?.map((year) => +year),
      joinDate: this.convertDateRange(queryData.joinDate),
      commitDate: this.convertDateRange(queryData.commitDate),
      joinState: queryData.joinState
        ?.map((state) => {
          return JoinStateGroups[state];
        })
        .flat(),
      funnelStates: queryData.funnelStates
        ?.map((state) => {
          return FunnelStateMaps["SIDEBAR"][state];
        })
        .flat(),
    };
  }

  convertDateRange(
    dateRange: DateRange | undefined,
  ): DateRangeInput | undefined {
    if (!dateRange || Object.keys(dateRange).length === 0) return undefined;
    const dateRangeInput = {
      start: dateRange?.from || dateRange?.to,
      inclusiveEnd: dateRange?.to || dateRange?.from,
    };
    return dateRangeInput.start !== undefined &&
      dateRangeInput.inclusiveEnd !== undefined
      ? dateRangeInput
      : undefined;
  }

  toUrlQuery = () => ({
    ...this.removeEmptyFields(this.data),
  });

  clone = ({ data }: { data?: Partial<FilterInputData> }) =>
    new FilterState(data ? { ...this.data, ...data } : this.data);

  static fromUrlQuery(filters: ParsedQs): FilterState {
    let data = {};
    const filterDataParseResult = FilterInputData.safeParse(filters);
    if (filterDataParseResult.success) {
      data = filterDataParseResult.data;
    } else {
      console.error(`Could not parse filters from url query string.`);
    }

    return new FilterState(data);
  }

  merge(fs?: FilterState) {
    return this.clone({ data: this.removeEmptyFields(fs?.data || {}) });
  }

  formatRiseFilters(input: Partial<FilterInputData>) {
    const {
      applyIndex,
      riseCommitIndex: commitIndex,
      yieldIndex,
      meltIndex,
      upTrendingScores,
      downTrendingScores,
      ...rest
    } = input;

    const rise: RiseStudentListFiltersInput = {
      applyIndex,
      commitIndex,
      yieldIndex,
      meltIndex,
      upTrendingScores,
      downTrendingScores,
    };

    const emptyRemoved = Object.entries(rise).filter(
      ([, value]) => value !== undefined,
    );

    if (emptyRemoved.length > 0) {
      return { ...rest, rise: Object.fromEntries(emptyRemoved) };
    }
    return rest;
  }
}
